<template>
    <div id="add-ereceipt-modal">
        <b-modal id="modal-add-code"
                 ref="modal"
                 title="Přidat kód"
                 @hidden="resetModal"
                 @ok="handleOk"
                 @shown="shownAddCodeFocus"
                 :ok-disabled="okDisabled">

            <template v-slot:modal-cancel>
                Storno
            </template>

            <template v-slot:modal-header="{ close }">
                <div id="cameraButtonWrapper" class="d-flex align-items-center">
                    <!-- Kamera -->
                    <b-button id="cameraButton" size="sm"
                              :variant="showCamera ? 'danger' : 'info'"
                              class="mr-2"
                              @click="switchCamera"
                              :disabled="isCameraEnabled && disabledCameraText">
                        <i class="fa fa-camera"></i>
                    </b-button>

                    <!-- Galerie (vlastní input + tlačítko) -->
                    <input ref="fileInput"
                           type="file"
                           accept="image/*"
                           @change="onFileChange"
                           style="display:none" />
                    <b-button id="galleryButton"
                              size="sm"
                              variant="secondary"
                              class="mr-2"
                              @click="openFilePicker">
                        <i class="fa fa-image"></i>
                        Galerie
                    </b-button>

                    <span class="text-muted" v-if="selectedFileName">{{ selectedFileName }}</span>
                </div>

                <b-tooltip target="cameraButtonWrapper" triggers="hover" v-if="!isCameraEnabled && disabledCameraText">
                    {{disabledCameraText}}
                </b-tooltip>
                <h5 class="mb-0">Přidat kód</h5>
            </template>

            <form ref="form" @submit.stop.prevent="handleSubmit">
                <b-form-group :state="validationState"
                              label="Kód"
                              label-for="name-input"
                              :invalid-feedback="validationMessage">

                    <b-form-input id="eReceiptCode" ref="addCodeInput"
                                  v-model.trim="$v.addEItemCode.$model"
                                  :state="validationState"
                                  @keyup="inputChange"
                                  :min-length="12"
                                  autocomplete="off"
                                  :formatter="formatter"></b-form-input>
                    <span class="text-danger">{{disabledCameraText}}</span>
                </b-form-group>
            </form>

            <StreamBarcodeReader v-if="showCamera" @decode="onDecode" @loaded="onInit">
                <div class="loading-indicator text-center" v-if="cameraLoading">
                    <strong>Zapínám kameru...</strong>
                </div>
            </StreamBarcodeReader>
        </b-modal>
    </div>
</template>

<script>
    import { required } from "vuelidate/lib/validators";
    import { eitemCodeValidatorService } from '@/services'
    import { StreamBarcodeReader } from "vue-barcode-reader";
    // ZXing načteme až při použití (dynamic import), aby se zbytečně nezvětšoval bundle

    var DetectRTC = require('detectrtc');

    export default {
        name: 'AddItemCodeModal',
        components: {
            StreamBarcodeReader
        },
        data: function () {
            return {
                addEItemCode: "",
                validationState: null,
                validationMessage: "",
                modalFormValidFromServer: false,
                showCamera: false,
                isCameraEnabled: false,
                disabledCameraText: null,
                cameraLoading: false,
                okDisabled: false,
                selectedFileName: ""
            }
        },
        props: {
            eDocuments: {
                default: () => [],
                type: Array,
                required: false
            },
            eReceiptIsAllowed: {
                required: false
            },
            eVoucherIsAllowed: {
                required: false
            }
        },
        validations: {
            addEItemCode: {
                required,
                lengthValidity(value) {
                    // Odstraníme mezery pro kontrolu délky
                    const codeWithoutSpaces = value ? value.replace(/\s/g, '') : '';
                    const length = codeWithoutSpaces.length;
                    return length === 9 || length === 12; // 9 eVoucher / 12 ePrescription
                },
                isServerValid(value) {
                    return this.modalFormValidFromServer
                }
            }
        },
        methods: {
            formatter(value) {
                return value.toUpperCase()
            },
            async onInit(promise) {
                this.cameraLoading = true
                try {
                    await promise
                } catch (error) {
                    this.handleError(error)
                }
                finally {
                    this.cameraLoading = false
                }
            },
            handleError(error) {
                let errorTypes = {
                    NotAllowedError: "CHYBA: musíte udělit oprávnění pro přístup ke kameře",
                    NotFoundError: "CHYBA: zařízení nemá žádnou kameru",
                    NotSupportedError: "CHYBA: vyžadován zabezpečený kontext (HTTPS, localhost)",
                    NotReadableError: "CHYBA: zkontrolujte, zda se kamera již nepoužívá",
                    OverconstrainedError: "CHYBA: vaše kamera je nevhodná",
                    StreamApiNotSupportedError: "CHYBA: Stream API není v tomto prohlížeči podporován"
                }
                this.disabledCameraText = errorTypes[error.name] || "Neznámá chyba"
            },
            resetModal() {
                this.addEItemCode = ''
                this.validationState = null
                this.selectedFileName = ""
            },
            shownAddCodeFocus() {
                this.$refs.addCodeInput && this.$refs.addCodeInput.focus()
                this.okDisabled = true;
            },
            async handleOk(bvModalEvt) {
                bvModalEvt.preventDefault()
                await this.handleSubmit()
            },
            async handleSubmit() {
                this.okDisabled = true;

                let codes = this.addEItemCode.split(",");
                let promises = codes.map(code => this.validateAndAddCode(code));

                try {
                    await Promise.all(promises)

                    this.$nextTick(() => {
                        this.$bvModal.hide('modal-add-code')
                    })
                } catch (e) {
                    console.log("Chyba", e);
                    this.okDisabled = true;
                } finally {
                    this.okDisabled = true;
                }
            },
            async validateAndAddCode(code) {
                code = code.replace(/\s/g, '');
                if (this.isCodeAlreadyAdded(code)) {
                    this.setValidationMessage("Kód je již na seznamu.", false)
                    return
                }

                this.addEItemCode = code
                let result = await this.checkFormValidity(code);

                if (!result.isValid) {
                    return
                }

                this.eDocuments.push({ code: code, eItemShortcut: result.eItemShortcut });
            },
            isCodeAlreadyAdded(code) {
                return this.eDocuments.some(item => item.code === code)
            },
            async checkFormValidity(code) {
                this.okDisabled = true;
                this.$v.addEItemCode.$touch();

                let result = await eitemCodeValidatorService.isEItemCodeValid(code);
                this.modalFormValidFromServer = result.data.isValid;
                let valid = result.data.isValid && !this.$v.addEItemCode.$invalid

                if (!valid) {
                    this.setValidationMessageByError()
                }
                else {
                    this.okDisabled = false;
                }

                this.validationState = valid

                return result.data
            },
            setValidationMessageByError() {
                if (!this.$v.addEItemCode.required) {
                    this.setValidationMessage("Kód musí být vyplněný.", false)
                }
                else if (!this.$v.addEItemCode.lengthValidity) {
                    this.setValidationMessage("Délka kódu neodpovídá ePoukazu ani eReceptu.", false)
                }
                else if (!this.$v.addEItemCode.isServerValid) {
                    this.setValidationMessage("Kód není validní.", false)
                }
            },
            setValidationMessage(message, valid) {
                this.validationMessage = message
                this.validationState = valid
                this.okDisabled = !valid;
            },
            checkEVoucher(code) {
                if (code.length === 9 && !this.eVoucherIsAllowed) {
                    this.setValidationMessage("Vámi vybraná provozovna nepodporuje ePoukazy.", false)
                    return false;
                }

                return true;
            },
            checkEReceipt(code) {
                if (code.length === 12 && !this.eReceiptIsAllowed) {
                    this.setValidationMessage("Vámi vybraná provozovna nepodporuje eRecepty.", false)
                    return false;
                }

                return true;
            },
            async inputChange() {
                this.modalFormValidFromServer = false;

                let codes = String(this.$v.addEItemCode.$model).split(',').map(s => s.replace(/\s+/g, ''));
                let valid = true;

                for (let code of codes) {
                    if (code.length === 9) {
                        if (!this.checkEVoucher(code)) {
                            valid = false;
                            break;
                        }
                    } else if (code.length === 12) {
                        if (!this.checkEReceipt(code)) {
                            valid = false;
                            break;
                        }
                    }
                    else {
                        this.setValidationMessage("Kód musí mít 9 nebo 12 znaků.", false);
                        valid = false;
                        break;
                    }

                    let result = await eitemCodeValidatorService.isEItemCodeValid(code);
                    if (!result.data.isValid) {
                        this.setValidationMessage("Kód: " + code + " není validní", false);
                        valid = false;
                        break;
                    }
                }

                this.modalFormValidFromServer = valid;

                this.$v.addEItemCode.$touch();

                if (valid) {
                    this.setValidationMessage("Všechny kódy jsou validní.", true);
                }

                this.validationState = valid;
            },

            // --- DEKÓDOVÁNÍ ---

            // Výsledek z kamery
            async onDecode(result) {
                await this.handleDecoded(result);
            },

            // Společná logika pro parsování a následnou validaci
            async handleDecoded(result) {
                this.showCamera = false;

                if (typeof result === 'string' && result.startsWith("https") && result.includes('erp?')) {
                    this.addEItemCode = result.substring(
                        result.lastIndexOf("i=") + 2,
                        result.lastIndexOf("&d"))
                }
                else if (typeof result === 'string' && result.startsWith("https") && result.includes('epo?')) {
                    this.addEItemCode = result.substring(
                        result.lastIndexOf("i=") + 2,
                        result.lastIndexOf("&s"))
                }
                else if (typeof result === 'string' && result.startsWith("PRUV")) {
                    const pattern = /SERP:(?<erecept>.{12})\\*/gm;
                    const matches = result.match(pattern) || [];
                    const mapped = matches.map(m => m.replace("SERP:", ""));
                    this.addEItemCode = mapped.join(',');
                }
                else {
                    // Obecný fallback (1D/QR text, nebo více kódů spojených čárkou)
                    this.addEItemCode = String(result);
                }

                await this.inputChange();
            },

            // --- GALERIE (vlastní implementace s ZXing) ---
            openFilePicker() {
                this.$refs.fileInput && this.$refs.fileInput.click();
            },
            async onFileChange(e) {
                const file = e.target.files && e.target.files[0];
                this.selectedFileName = file ? file.name : "";

                if (!file) return;

                try {
                    const url = URL.createObjectURL(file);

                    // Načti ZXing až teď
                    const { BrowserMultiFormatReader, NotFoundException } = await import(/* webpackChunkName: "zxing" */ '@zxing/library');

                    const reader = new BrowserMultiFormatReader();
                    // decodeFromImageUrl vrátí první nalezený kód (1D i QR)
                    const result = await reader.decodeFromImageUrl(url);

                    URL.revokeObjectURL(url);

                    await this.handleDecoded(result.getText ? result.getText() : String(result));
                } catch (err) {
                    console.error(err);
                    this.setValidationMessage("Nepodařilo se přečíst kód z obrázku.", false);
                } finally {
                    // vyresetujeme input, aby šlo vybrat znovu ten samý soubor
                    if (this.$refs.fileInput) this.$refs.fileInput.value = '';
                }
            },

            // --- KAMERA ---

            switchCamera() {
                this.showCamera = !this.showCamera;
                if (this.showCamera) {
                    this.getCameraInfo();
                }
            },
            checkIfCameraAvailable() {
                DetectRTC.load(() => {
                    this.isCameraEnabled = DetectRTC.isWebRTCSupported && DetectRTC.hasWebcam;
                    if (!this.isCameraEnabled) {
                        this.disabledCameraText = 'Kamera není dostupná';
                    }
                });
            },
            getCameraInfo() {
                let thisHelper = this;

                DetectRTC.load(function () {
                    if (DetectRTC.hasWebcam && DetectRTC.isWebsiteHasWebcamPermissions) {
                        thisHelper.isCameraEnabled = true;
                        thisHelper.disabledCameraText = null;
                    }
                    else {
                        thisHelper.isCameraEnabled = false;
                        thisHelper.showCamera = false;

                        if (!DetectRTC.hasWebcam) {
                            thisHelper.disabledCameraText = "Vaše zařízení nemá připojenou kameru.";
                        }

                        if (!DetectRTC.isWebsiteHasWebcamPermissions) {
                            thisHelper.disabledCameraText = "Nebyl udělen souhlas pro použití kamery na této stránce.";
                        }
                    }

                });
            },

        },
        created() {
            // this.checkIfCameraAvailable();
        }
    }
</script>
